import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { MissionsCatalog, PriorityDef, StatusDef } from './missionTypes.ts';
import { PILL_STYLE_LABELS } from './missionTypes.ts';
import { addPriority, addStatus, clampVariant, toneForPerson } from './missionStorage.ts';

export type MondayField = 'person' | 'status' | 'priority';

function pillClass(variant: number): string {
  const v = clampVariant(variant);
  return `missions-pill missions-pill--v${v}`;
}

/** Single letter for compact person chip (column trigger) */
function firstLetter(name: string): string {
  const t = name.trim();
  if (!t) return '?';
  return t[0].toUpperCase();
}

function useMobileSheet(breakpoint = 719): boolean {
  const [m, setM] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(`(max-width: ${breakpoint}px)`).matches : false
  );
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const fn = () => setM(mq.matches);
    mq.addEventListener('change', fn);
    fn();
    return () => mq.removeEventListener('change', fn);
  }, [breakpoint]);
  return m;
}

type Props = {
  field: MondayField;
  catalog: MissionsCatalog;
  value: string;
  onPick: (next: string) => void;
  onApplyCatalog: (next: MissionsCatalog) => void;
  ariaLabel: string;
  /** Unique id for aria-controls */
  instanceId: string;
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
};

export const MondayMissionDropdown: React.FC<Props> = ({
  field,
  catalog,
  value,
  onPick,
  onApplyCatalog,
  ariaLabel,
  instanceId,
  isOpen,
  onOpen,
  onClose,
}) => {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [editMode, setEditMode] = useState(false);
  const [draftPeople, setDraftPeople] = useState<string[]>([]);
  const [draftStatuses, setDraftStatuses] = useState<StatusDef[]>([]);
  const [draftPriorities, setDraftPriorities] = useState<PriorityDef[]>([]);
  const [draftPersonTones, setDraftPersonTones] = useState<number[]>([]);
  const [panelPos, setPanelPos] = useState({ top: 0, left: 0, width: 280, arrowLeft: 24 });
  const [popoverLayoutDone, setPopoverLayoutDone] = useState(false);
  const isSheet = useMobileSheet(719);

  const syncDraftFromCatalog = useCallback(() => {
    setDraftPeople([...catalog.people]);
    setDraftStatuses(JSON.parse(JSON.stringify(catalog.statuses)));
    setDraftPriorities(JSON.parse(JSON.stringify(catalog.priorities)));
    const tones = catalog.personTones ?? catalog.people.map((_, i) => i % 8);
    setDraftPersonTones(tones.slice(0, catalog.people.length));
  }, [catalog]);

  useEffect(() => {
    if (isOpen) syncDraftFromCatalog();
    else setEditMode(false);
  }, [isOpen, syncDraftFromCatalog]);

  useLayoutEffect(() => {
    if (!isOpen) {
      setPopoverLayoutDone(false);
      return;
    }
    if (isSheet) {
      setPopoverLayoutDone(true);
      return;
    }
    const el = triggerRef.current;
    if (!el) {
      setPopoverLayoutDone(false);
      return;
    }
    const rect = el.getBoundingClientRect();
    const width = Math.max(Math.min(rect.width, 340), editMode ? 300 : 260);
    let left = rect.left;
    if (left + width > window.innerWidth - 12) left = window.innerWidth - width - 12;
    if (left < 8) left = 8;
    const triggerCx = rect.left + rect.width / 2;
    const arrowLeft = Math.min(Math.max(triggerCx - left - 8, 14), width - 30);
    setPanelPos({
      top: rect.bottom + 6,
      left,
      width,
      arrowLeft,
    });
    setPopoverLayoutDone(true);
  }, [isOpen, isSheet, editMode]);

  /** Keep popover bottom edge on-screen (Apply / footer must stay reachable). */
  useLayoutEffect(() => {
    if (!isOpen || isSheet || !popoverLayoutDone || !panelRef.current) return;
    const panel = panelRef.current;
    const pad = 10;
    const br = panel.getBoundingClientRect();
    const maxBottom = window.innerHeight - pad;
    if (br.bottom <= maxBottom) return;
    const shift = br.bottom - maxBottom;
    setPanelPos((p) => {
      const nextTop = Math.max(pad, p.top - shift);
      return nextTop === p.top ? p : { ...p, top: nextTop };
    });
  }, [isOpen, isSheet, popoverLayoutDone, editMode]);

  useEffect(() => {
    if (!isOpen) return;
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen || !isSheet) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen, isSheet]);

  const statusDef = useMemo(
    () => catalog.statuses.find((s) => s.id === value) ?? catalog.statuses[0],
    [catalog.statuses, value]
  );
  const priorityDef = useMemo(
    () => catalog.priorities.find((p) => p.id === value) ?? catalog.priorities[0],
    [catalog.priorities, value]
  );

  const triggerLabel = useMemo(() => {
    if (field === 'person') return value || catalog.people[0];
    if (field === 'status') return statusDef?.label ?? '';
    return priorityDef?.label ?? '';
  }, [field, value, catalog.people, statusDef, priorityDef]);

  const applyPick = (next: string) => {
    onPick(next);
    onClose();
  };

  const handleApplyEdit = () => {
    let next: MissionsCatalog = { ...catalog };
    if (field === 'person') {
      const outP: string[] = [];
      const outT: number[] = [];
      for (let i = 0; i < draftPeople.length; i++) {
        const n = draftPeople[i].trim();
        if (!n) continue;
        outP.push(n);
        outT.push(clampVariant(draftPersonTones[i] ?? i % 8));
      }
      if (outP.length === 0) {
        outP.push('Unassigned');
        outT.push(0);
      }
      next = { ...catalog, people: outP, personTones: outT };
    } else if (field === 'status') {
      next = { ...catalog, statuses: draftStatuses.length ? draftStatuses : catalog.statuses };
    } else {
      next = { ...catalog, priorities: draftPriorities.length ? draftPriorities : catalog.priorities };
    }
    onApplyCatalog(next);
    setEditMode(false);
    syncDraftFromCatalog();
  };

  if (!isOpen) {
    return (
      <button
        ref={triggerRef}
        type="button"
        id={`${instanceId}-trigger`}
        className={
          field === 'person'
            ? 'missions-dd-trigger missions-dd-trigger--person missions-dd-trigger--avatar-only'
            : `missions-dd-trigger missions-dd-trigger--pill ${pillClass(
                field === 'status' ? statusDef.variant : priorityDef.variant
              )}`
        }
        aria-haspopup="listbox"
        aria-expanded={false}
        aria-label={ariaLabel}
        onClick={(e) => {
          e.stopPropagation();
          onOpen();
        }}
      >
        {field === 'person' ? (
          <>
            <span className={`missions-avatar missions-avatar--tone-${toneForPerson(catalog, triggerLabel)} missions-avatar--solo`}>
              {firstLetter(triggerLabel)}
            </span>
            <span className="missions-dd-chevron" aria-hidden />
          </>
        ) : (
          <>
            <span className="missions-dd-trigger-text">{triggerLabel}</span>
            <span className="missions-dd-chevron" aria-hidden />
          </>
        )}
      </button>
    );
  }

  const overlay = (
    <>
      <div
        className={`missions-dd-backdrop ${isSheet ? 'missions-dd-backdrop--sheet' : ''}`}
        role="presentation"
        onMouseDown={(e) => {
          e.preventDefault();
          onClose();
        }}
      />
      <div
        ref={panelRef}
        id={`${instanceId}-panel`}
        role="listbox"
        aria-labelledby={`${instanceId}-trigger`}
        className={`missions-dd-panel ${isSheet ? 'missions-dd-panel--sheet' : 'missions-dd-panel--popover'}`}
        style={
          isSheet
            ? undefined
            : {
                position: 'fixed',
                top: panelPos.top,
                left: panelPos.left,
                width: panelPos.width,
              }
        }
        onMouseDown={(e) => e.stopPropagation()}
      >
        {!isSheet && <div className="missions-dd-arrow" style={{ left: panelPos.arrowLeft }} />}
        {!editMode ? (
          <>
            <div className="missions-dd-pick-list">
              {field === 'person' &&
                catalog.people.map((p) => (
                  <button
                    key={p}
                    type="button"
                    role="option"
                    aria-selected={value === p}
                    className={`missions-dd-pick-row ${value === p ? 'missions-dd-pick-row--active' : ''}`}
                    onClick={() => applyPick(p)}
                  >
                    <span className={`missions-avatar missions-avatar--tone-${toneForPerson(catalog, p)} missions-avatar--solo`}>
                      {firstLetter(p)}
                    </span>
                    <span>{p}</span>
                  </button>
                ))}
              {field === 'status' &&
                catalog.statuses.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    role="option"
                    aria-selected={value === s.id}
                    className={`missions-dd-pick-status ${pillClass(s.variant)}`}
                    onClick={() => applyPick(s.id)}
                  >
                    {s.label}
                  </button>
                ))}
              {field === 'priority' &&
                catalog.priorities.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    role="option"
                    aria-selected={value === s.id}
                    className={`missions-dd-pick-status ${pillClass(s.variant)}`}
                    onClick={() => applyPick(s.id)}
                  >
                    {s.label}
                  </button>
                ))}
            </div>
            <button type="button" className="missions-dd-edit-link" onClick={() => setEditMode(true)}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path
                  d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"
                  fill="currentColor"
                />
              </svg>
              Edit labels
            </button>
            <div className="missions-dd-divider" />
            <button type="button" className="missions-dd-footer-opt" disabled title="Coming soon">
              <span className="missions-dd-sparkle" aria-hidden>
                ✦
              </span>
              Auto-assign labels
            </button>
          </>
        ) : (
          <div className="missions-dd-edit">
            <div className="missions-dd-edit-scroll">
              <div className="missions-dd-edit-header">
                <button type="button" className="missions-dd-back" onClick={() => setEditMode(false)}>
                  ← Back
                </button>
              </div>
              {field === 'person' && (
                <ul className="missions-dd-edit-list">
                  {draftPeople.map((p, i) => (
                    <li key={i} className="missions-dd-edit-row missions-dd-edit-row--person">
                      <span className={`missions-avatar missions-avatar--tone-${clampVariant(draftPersonTones[i] ?? i % 8)} missions-avatar--solo`}>
                        {firstLetter(p)}
                      </span>
                      <input
                        type="text"
                        className="missions-input missions-dd-edit-input"
                        value={p}
                        onChange={(e) =>
                          setDraftPeople((prev) => {
                            const n = [...prev];
                            n[i] = e.target.value;
                            return n;
                          })
                        }
                        aria-label={`Person ${i + 1}`}
                      />
                      <div className="missions-dd-variant-grid missions-dd-variant-grid--person" role="group" aria-label="Avatar color">
                        {PILL_STYLE_LABELS.map((_, vi) => (
                          <button
                            key={vi}
                            type="button"
                            className={`missions-dd-variant-dot missions-dd-v${vi} ${clampVariant(draftPersonTones[i] ?? 0) === vi ? 'missions-dd-variant-dot--on' : ''}`}
                            aria-label={PILL_STYLE_LABELS[vi]}
                            aria-pressed={clampVariant(draftPersonTones[i] ?? 0) === vi}
                            onClick={() =>
                              setDraftPersonTones((prev) => {
                                const next = [...prev];
                                while (next.length <= i) next.push(0);
                                next[i] = clampVariant(vi);
                                return next;
                              })
                            }
                          />
                        ))}
                      </div>
                      <button
                        type="button"
                        className="missions-btn missions-btn--icon"
                        disabled={draftPeople.length <= 1}
                        onClick={() => {
                          setDraftPeople((prev) => prev.filter((_, j) => j !== i));
                          setDraftPersonTones((prev) => prev.filter((_, j) => j !== i));
                        }}
                      >
                        −
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {(field === 'status' || field === 'priority') && (
                <ul className="missions-dd-edit-list">
                  {(field === 'status' ? draftStatuses : draftPriorities).map((item, i) => (
                    <li key={item.id} className="missions-dd-edit-row missions-dd-edit-row--status">
                      <div className="missions-dd-swatch-wrap">
                        <span className={`missions-dd-swatch missions-dd-v${clampVariant(item.variant)}`} title="Style" />
                        <input
                          type="text"
                          className="missions-input missions-dd-edit-input"
                          value={item.label}
                          onChange={(e) => {
                            const v = e.target.value;
                            if (field === 'status') {
                              setDraftStatuses((prev) => prev.map((s, j) => (j === i ? { ...s, label: v } : s)));
                            } else {
                              setDraftPriorities((prev) => prev.map((s, j) => (j === i ? { ...s, label: v } : s)));
                            }
                          }}
                          aria-label="Label"
                        />
                      </div>
                      <div className="missions-dd-variant-grid" role="group" aria-label="Color style">
                        {PILL_STYLE_LABELS.map((_, vi) => (
                          <button
                            key={vi}
                            type="button"
                            className={`missions-dd-variant-dot missions-dd-v${vi} ${item.variant === vi ? 'missions-dd-variant-dot--on' : ''}`}
                            aria-label={PILL_STYLE_LABELS[vi]}
                            aria-pressed={item.variant === vi}
                            onClick={() => {
                              if (field === 'status') {
                                setDraftStatuses((prev) =>
                                  prev.map((s, j) => (j === i ? { ...s, variant: clampVariant(vi) } : s))
                                );
                              } else {
                                setDraftPriorities((prev) =>
                                  prev.map((s, j) => (j === i ? { ...s, variant: clampVariant(vi) } : s))
                                );
                              }
                            }}
                          />
                        ))}
                      </div>
                      <button
                        type="button"
                        className="missions-btn missions-btn--icon"
                        disabled={(field === 'status' ? draftStatuses : draftPriorities).length <= 1}
                        onClick={() => {
                          if (field === 'status') {
                            setDraftStatuses((prev) => prev.filter((_, j) => j !== i));
                          } else {
                            setDraftPriorities((prev) => prev.filter((_, j) => j !== i));
                          }
                        }}
                      >
                        −
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <button
                type="button"
                className="missions-dd-new-label"
                onClick={() => {
                  if (field === 'person') {
                    setDraftPeople((p) => [...p, 'New person']);
                    setDraftPersonTones((t) => [...t, 0]);
                  } else if (field === 'status') setDraftStatuses((p) => [...p, addStatus(catalog)]);
                  else setDraftPriorities((p) => [...p, addPriority(catalog)]);
                }}
              >
                + New label
              </button>
            </div>
            <div className="missions-dd-edit-actions">
              <button type="button" className="missions-dd-apply" onClick={handleApplyEdit}>
                Apply
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        id={`${instanceId}-trigger`}
        className={
          field === 'person'
            ? 'missions-dd-trigger missions-dd-trigger--person missions-dd-trigger--avatar-only missions-dd-trigger--open'
            : `missions-dd-trigger missions-dd-trigger--pill missions-dd-trigger--open ${pillClass(
                field === 'status' ? statusDef.variant : priorityDef.variant
              )}`
        }
        aria-haspopup="listbox"
        aria-expanded={true}
        aria-controls={`${instanceId}-panel`}
        aria-label={ariaLabel}
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
      >
        {field === 'person' ? (
          <>
            <span className={`missions-avatar missions-avatar--tone-${toneForPerson(catalog, triggerLabel)} missions-avatar--solo`}>
              {firstLetter(triggerLabel)}
            </span>
            <span className="missions-dd-chevron missions-dd-chevron--up" aria-hidden />
          </>
        ) : (
          <>
            <span className="missions-dd-trigger-text">{triggerLabel}</span>
            <span className="missions-dd-chevron missions-dd-chevron--up" aria-hidden />
          </>
        )}
      </button>
      {typeof document !== 'undefined' &&
        isOpen &&
        (isSheet || popoverLayoutDone) &&
        createPortal(<div className="missions-dd-theme">{overlay}</div>, document.body)}
    </>
  );
};
